import dynamic from 'next/dynamic'

import type { FC, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import type { Product } from '@/types'
import type { DesignHotspot } from '@/components/three/doll-designs'
import type { Quality } from '@/components/three/textures'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Hand, ImageIcon, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ProductImage } from '@/components/shop/product-image'

import {
    detectQuality,
    prefersReducedMotion,
    supportsWebGL
} from '@/lib/webgl'

const ProductViewer = dynamic(() => import('@/components/three/product-viewer'), { ssr: false, loading: () => null })

interface ProductMediaProps {
    product: Product
    colorHex: string | null
}

type MediaItem = { kind: 'photo', src: string, alt: string } | { kind: 'render' } | { kind: 'viewer' }

export const ProductMedia: FC<ProductMediaProps> = ({ product, colorHex }): ReactNode => {
    const photos: Array<MediaItem> = [product.image, ...product.gallery]
        .filter((src): src is string => Boolean(src))
        .map((src, index) => ({
            kind: 'photo',
            src,
            alt: (index === 0 ? product.imageAlt : product.galleryAlt[index - 1]) ?? `${product.name}, photo ${index + 1}`
        }))

    const items: Array<MediaItem> = photos.length ? [...photos, { kind: 'viewer' }] : [{ kind: 'viewer' }, { kind: 'render' }]
    const [active, setActive] = useState(0)
    const [webgl, setWebgl] = useState<boolean | null>(null)
    const [quality, setQuality] = useState<Quality>('medium')
    const [reducedMotion, setReducedMotion] = useState(false)
    const [inView, setInView] = useState(false)
    const [hotspots, setHotspots] = useState<Array<DesignHotspot>>([])
    const [openHotspot, setOpenHotspot] = useState<string | null>(null)
    const [touched, setTouched] = useState(false)
    const [resetSignal, setResetSignal] = useState(0)
    const stage = useRef<HTMLDivElement>(null)
    const nudge = useRef(0)
    const hotspotElements = useRef(new Map<string, HTMLElement>())

    useEffect(() => {
        setWebgl(supportsWebGL())
        setQuality(detectQuality())
        setReducedMotion(prefersReducedMotion())
    }, [])

    // Only load the 3D viewer once the gallery is on screen.
    useEffect(() => {
        const node = stage.current

        if (!node || !('IntersectionObserver' in window)) {
            setInView(true)

            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true)
                observer.disconnect()
            }
        }, { rootMargin: '200px' })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        setActive(0)
        setOpenHotspot(null)
    }, [product.id])

    const current = items[Math.min(active, items.length - 1)]
    const showViewer = current.kind === 'viewer' && webgl !== false && inView

    const handleReady = useCallback((list: Array<DesignHotspot>) => setHotspots(list), [])
    const handleInteract = useCallback(() => setTouched(true), [])

    const registerHotspot = useCallback((id: string) => (element: HTMLButtonElement | null) => {
        if (element) hotspotElements.current.set(id, element)
        else hotspotElements.current.delete(id)
    }, [])

    const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (current.kind !== 'viewer') return

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            nudge.current = -0.09
            setTouched(true)
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            nudge.current = 0.09
            setTouched(true)
        }
    }

    const openInfo = openHotspot ? hotspots.find(hotspot => hotspot.id === openHotspot) ?? null : null

    return (
        <div className='flex flex-col gap-4'>
            <div
                ref={stage}
                tabIndex={current.kind === 'viewer' ? 0 : -1}
                onKeyDown={onKeyDown}
                aria-label={current.kind === 'viewer' ? `Interactive 3D view of ${product.name}. Drag, or use the left and right arrow keys, to rotate.` : undefined}
                role={current.kind === 'viewer' ? 'application' : undefined}
                className='relative aspect-square w-full overflow-hidden rounded-[36px] bg-[radial-gradient(110%_90%_at_50%_15%,#fffaf3_0%,#f9ebe1_55%,#efd9cb_100%)] shadow-[0_30px_70px_-45px_rgba(58,40,30,0.6)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-caramel-300/60'
            >
                {current.kind === 'photo' && (
                    <img src={current.src} alt={current.alt} className='absolute inset-0 h-full w-full object-cover' />
                )}

                {current.kind === 'render' && (
                    <ProductImage
                        src={null}
                        alt={`${product.name}, studio view`}
                        designKey={product.designKey}
                        mainColor={colorHex}
                        accentColor={product.accentColor}
                        backdrop='none'
                        className='absolute inset-0'
                    />
                )}

                {current.kind === 'viewer' && (
                    <>
                        {showViewer
                            ? (
                                <div className='absolute inset-0'>
                                    <ProductViewer
                                        designKey={product.designKey}
                                        mainColor={colorHex}
                                        accentColor={product.accentColor}
                                        quality={quality}
                                        reducedMotion={reducedMotion}
                                        resetSignal={resetSignal}
                                        nudge={nudge}
                                        hotspotElements={hotspotElements}
                                        onReady={handleReady}
                                        onInteract={handleInteract}
                                    />
                                </div>
                            )
                            : (
                                <ProductImage
                                    src={null}
                                    alt={`${product.name}, studio view`}
                                    designKey={product.designKey}
                                    mainColor={colorHex}
                                    accentColor={product.accentColor}
                                    backdrop='none'
                                    className='absolute inset-0'
                                />
                            )}

                        {showViewer && hotspots.map((hotspot, index) => (
                            <button
                                key={hotspot.id}
                                ref={registerHotspot(hotspot.id)}
                                type='button'
                                data-facing='false'
                                onClick={() => setOpenHotspot(current => (current === hotspot.id ? null : hotspot.id))}
                                aria-expanded={openHotspot === hotspot.id}
                                aria-label={hotspot.title}
                                className='group absolute left-0 top-0 z-10 transition-opacity duration-300 data-[facing=false]:pointer-events-none data-[facing=false]:opacity-0'
                            >
                                <span className='relative -ml-4 -mt-4 grid size-8 place-items-center'>
                                    <span className='absolute inset-0 rounded-full bg-white/70 motion-safe:animate-hotspot' />
                                    <span className={cn(
                                        'relative grid size-7 place-items-center rounded-full text-xs font-bold shadow-[0_4px_14px_rgba(58,40,30,0.3)] ring-2 transition',
                                        openHotspot === hotspot.id ? 'bg-cocoa-800 text-cream-50 ring-cocoa-800' : 'bg-white text-blush-600 ring-blush-200'
                                    )}>
                                        {index + 1}
                                    </span>
                                </span>
                            </button>
                        ))}

                        {showViewer && openInfo && (
                            <div className='absolute inset-x-4 bottom-4 z-20 rounded-3xl bg-cream-50/95 p-5 shadow-xl backdrop-blur sm:inset-x-auto sm:left-5 sm:max-w-xs' role='status'>
                                <p className='font-heading text-lg text-cocoa-900'>
                                    {openInfo.title}
                                </p>
                                <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                    {openInfo.text}
                                </p>
                            </div>
                        )}

                        <div className='absolute right-4 top-4 z-20 flex items-center gap-2'>
                            {showViewer && (
                                <button
                                    type='button'
                                    onClick={() => {
                                        setResetSignal(value => value + 1)
                                        setOpenHotspot(null)
                                    }}
                                    aria-label='Reset the view'
                                    className='grid size-10 place-items-center rounded-full bg-white/85 text-cocoa-800 shadow-sm backdrop-blur transition hover:bg-white'
                                >
                                    <RotateCcw className='size-4' />
                                </button>
                            )}
                        </div>

                        {webgl === false && (
                            <p className='absolute inset-x-6 bottom-6 z-10 rounded-2xl bg-cream-50/90 px-4 py-3 text-center text-sm text-cocoa-700'>
                                This browser cannot show the 3D view, so here is a studio picture instead.
                            </p>
                        )}

                        {showViewer && (
                            <p className={cn(
                                'pointer-events-none absolute left-1/2 top-5 z-10 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-cocoa-900/80 px-4 py-2 text-xs font-semibold text-cream-50 backdrop-blur transition-opacity duration-500',
                                touched ? 'opacity-0' : 'opacity-100'
                            )}>
                                <Hand className='size-4 text-blush-200' />
                                Drag to turn the doll 360 degrees
                            </p>
                        )}
                    </>
                )}
            </div>

            <div className='flex gap-3' role='tablist' aria-label='Product views'>
                {items.map((item, index) => {
                    const selected = index === active

                    return (
                        <button
                            key={`${item.kind}-${index}`}
                            type='button'
                            role='tab'
                            aria-selected={selected}
                            aria-label={item.kind === 'viewer' ? '360 degree view' : item.kind === 'render' ? 'Studio view' : `Photo ${index + 1}`}
                            onClick={() => {
                                setActive(index)
                                setOpenHotspot(null)
                            }}
                            className={cn(
                                'relative size-20 overflow-hidden rounded-2xl border-2 bg-cream-100 transition sm:size-24',
                                selected ? 'border-cocoa-800' : 'border-transparent hover:border-cocoa-700/30'
                            )}
                        >
                            {item.kind === 'photo' && <img src={item.src} alt='' className='absolute inset-0 h-full w-full object-cover' />}
                            {item.kind === 'render' && (
                                <>
                                    <ProductImage src={null} alt='' designKey={product.designKey} mainColor={colorHex} accentColor={product.accentColor} className='absolute inset-0' />
                                    <ImageIcon className='absolute bottom-1.5 right-1.5 size-4 text-cocoa-600' />
                                </>
                            )}
                            {item.kind === 'viewer' && (
                                <span className='absolute inset-0 grid place-items-center bg-[radial-gradient(circle_at_50%_40%,#fdf1f2,#f1dccf)]'>
                                    <span className='flex flex-col items-center gap-1 text-cocoa-800'>
                                        <Box className='size-6' />
                                        <span className='text-[11px] font-bold'>360°</span>
                                    </span>
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>

            {hotspots.length > 0 && current.kind === 'viewer' && showViewer && (
                <ol className='grid gap-2 sm:grid-cols-3'>
                    {hotspots.map((hotspot, index) => (
                        <li key={hotspot.id}>
                            <button
                                type='button'
                                onClick={() => setOpenHotspot(current => (current === hotspot.id ? null : hotspot.id))}
                                className={cn(
                                    'flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition',
                                    openHotspot === hotspot.id ? 'border-cocoa-800 bg-white' : 'border-border bg-white/60 hover:bg-white'
                                )}
                            >
                                <span className='grid size-6 shrink-0 place-items-center rounded-full bg-blush-100 text-xs font-bold text-blush-600'>
                                    {index + 1}
                                </span>
                                <span className='text-sm font-semibold text-cocoa-800'>
                                    {hotspot.title}
                                </span>
                            </button>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    )
}
